import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { AppContext } from "../../context/app.context";
import { computeEventDiff } from "../../utility/computeEventDiff";
import {
  getEffectiveChangedBy,
  stripUsernameDomain,
} from "../../utility/computeScheduleDiff";
import { formatDiffValue } from "../../components/EventHistoryList";
import { classifyEventDirection } from "../../utility/ticketMaster/classifyEventDirection";
import { groupTimeline, parsePayload } from "../../utility/ticketMaster/groupTimeline";
import PrettyJson from "./PrettyJson";
import GridSpinner from "../../components/GridSpinner";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// Ticket Master (#276) — correlated timeline, CONTENT-grouped.
//
// Each ROW is one logical action, assembled by matching data across the three
// collections (not time — a queued TurboQ message can land days later). The
// three lane cells hold that group's item(s); identical retries stack in one
// cell. Row-level expand keeps columns aligned. Alternating banding adapts to
// light/dark (via AppContext.darkMode). Columns are resizable.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

const DIR_COLOR = { out: "#3b82f6", in: "#8b5cf6", neutral: "#16a34a" };
const NOISE_KEYS = new Set(["history", "_mongoUsername", "_lastChangedAt", "_lastChangedBy", "_changeSource"]);
const MIN_FR = 0.3;

function MiniSpinner() {
  return <span className="inline-block rounded-full animate-spin" style={{ width: 12, height: 12, border: "2px solid currentColor", borderTopColor: "transparent" }} />;
}
function fmtEtIso(ts) {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return String(ts ?? "—");
  return d.toLocaleString("en-US", { timeZone: "America/New_York", month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit", second: "2-digit" });
}
const opLabel = (op) => op === "create" ? "Created" : op === "update" ? "Edited" : op === "delete" ? "Deleted" : op || "—";

function DiffChip({ v, kind }) {
  const bg = kind === "before" ? "rgba(220,80,80,0.16)" : "rgba(60,180,90,0.18)";
  return <span className="px-1 rounded-sm" style={{ backgroundColor: bg }}>{formatDiffValue(v)}</span>;
}
function Dot({ color, children }) {
  return <span className="inline-flex items-center justify-center rounded-full font-bold text-black shrink-0" style={{ backgroundColor: color, width: 15, height: 15, fontSize: 10 }}>{children}</span>;
}
function Entry({ color, summary, children, open, onToggle, errored }) {
  return (
    <div role="button" tabIndex={0} onClick={onToggle}
      className="mb-1 rounded-sm px-1.5 py-1 cursor-pointer hover:bg-skin-base-hover"
      style={{ borderLeft: `3px solid ${errored ? "#dc2626" : color}`, backgroundColor: errored ? "rgba(220,38,38,0.16)" : "rgba(128,128,128,0.06)" }}>
      <div className="flex items-center gap-1.5 flex-wrap text-[11px]">{summary}</div>
      {open ? <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>{children}</div> : null}
    </div>
  );
}
function Detail({ label, children }) {
  return (
    <details className="mb-1">
      <summary className="cursor-pointer opacity-70 text-[11px]">{label}</summary>
      <div className="mt-1 rounded-sm p-1.5" style={{ backgroundColor: "rgba(128,128,128,0.12)" }}>{children}</div>
    </details>
  );
}

function EventEntry({ raw, open, onToggle }) {
  const dir = classifyEventDirection(raw);
  const color = DIR_COLOR[dir.dir];
  const inbound = dir.dir === "in";
  const who = stripUsernameDomain(getEffectiveChangedBy(raw)) || "unknown";
  const diff = useMemo(() => computeEventDiff(raw).filter((d) => !NOISE_KEYS.has(d.key)), [raw]);
  return (
    <Entry color={color} open={open} onToggle={onToggle}
      summary={<>
        <Dot color={color}>{dir.arrow}</Dot>
        <span className="font-mono">{fmtEtIso(raw.timestamp)}</span>
        <span className="font-semibold">{opLabel(raw.operation)}</span>
        {inbound
          ? <span className="opacity-70 italic">FileMaker response</span>
          : <span className="opacity-70 truncate">{who}</span>}
      </>}>
      {diff.length > 0 ? (
        <ul className="text-[11px] font-mono flex flex-col gap-0.5 mb-1.5">
          {diff.map(({ key, before, after }) => (
            <li key={key} className="wrap-break-word">
              <span className="font-semibold">{key}:</span>{" "}
              <DiffChip v={before} kind="before" /> → <DiffChip v={after} kind="after" />
            </li>
          ))}
        </ul>
      ) : <div className="text-[11px] italic opacity-55 mb-1">No business-field change.</div>}
      {raw.updateFields ? <Detail label="updateFields"><PrettyJson value={raw.updateFields} /></Detail> : null}
      {raw.before ? <Detail label="before"><PrettyJson value={raw.before} /></Detail> : null}
      {raw.after ? <Detail label="after"><PrettyJson value={raw.after} /></Detail> : null}
    </Entry>
  );
}

function ScheduleEntry({ raw, open, onToggle }) {
  const before = raw.StatusCodeBefore, after = raw.StatusCodeAfter;
  const hasTransition = (before != null && before !== "") || (after != null && after !== "");
  const isCurrent = String(raw.IsCurrent) === "1" || raw.IsCurrent === true;
  return (
    <Entry color="#d98a1e" open={open} onToggle={onToggle}
      summary={<>
        <span className="font-mono">{raw.CreationTimestamp || "—"}</span>
        {hasTransition ? <span className="px-1 rounded-sm" style={{ backgroundColor: "rgba(217,138,30,0.22)" }}>{before ?? "—"} → {after ?? "—"}</span> : null}
        {isCurrent ? <span className="text-[9px] uppercase font-bold" style={{ color: "#d98a1e" }}>current</span> : null}
        {raw.ActivityType ? <span className="opacity-70 truncate">{raw.ActivityType}</span> : null}
      </>}>
      <PrettyJson value={raw} />
    </Entry>
  );
}

// Detect a failed transaction (#291): the ErrorInd flag is set, or the Result
// JSON carries an error (errorCode / descriptor / errorMessage / OK:false).
// Surfaces WHY a script didn't take (e.g. "schedule events must be unique").
function turboFailure(raw) {
  const errInd = raw.error != null && String(raw.error).trim() !== "" && String(raw.error).trim() !== "0";
  const r = parsePayload(raw.result);
  const msg = r && (r.descriptor || r.errorMessage) ? (r.descriptor || r.errorMessage) : null;
  const resultErr = r && ((r.errorCode != null && r.errorCode !== 0) || r.OK === false);
  return { failed: errInd || Boolean(resultErr), msg };
}

function TurboEntry({ raw, open, onToggle }) {
  const fail = turboFailure(raw);
  // Transactions are FileMaker-side script runs (pushes to Mongo/IMS) → outbound.
  return (
    <Entry color={DIR_COLOR.out} errored={fail.failed} open={open} onToggle={onToggle}
      summary={<>
        <Dot color={fail.failed ? "#dc2626" : DIR_COLOR.out}>{fail.failed ? "✕" : "↑"}</Dot>
        <span className="font-mono">{raw.created || "—"}</span>
        <span className="truncate opacity-80">{raw.scriptName || "—"}</span>
        {fail.failed
          ? <span className="font-semibold" style={{ color: "#dc2626" }}>failed{fail.msg ? `: ${fail.msg}` : ""}</span>
          : <span className="opacity-55">ok</span>}
      </>}>
      <Detail label="ScriptParameter (input)"><PrettyJson value={raw.param} /></Detail>
      {raw.result ? <Detail label="Result (response)"><PrettyJson value={raw.result} /></Detail> : null}
      <div className="text-[11px] opacity-50 mt-1">
        file: {raw.fileName || "—"} · by: {raw.by || "—"}{raw.error ? ` · errInd: ${raw.error}` : ""}{raw.id ? ` · #${raw.id}` : ""}
      </div>
    </Entry>
  );
}

function LaneCell({ lane, items, open, onToggle }) {
  if (!items || items.length === 0) return <div className="opacity-25 text-[11px] select-none">·</div>;
  const LaneEntry = lane === "eventLog" ? EventEntry : lane === "schedule" ? ScheduleEntry : TurboEntry;
  return (
    <div className="flex flex-col">
      {items.map((it, i) => <LaneEntry key={i} raw={it.raw} open={open} onToggle={onToggle} />)}
    </div>
  );
}

const LANES = [
  { key: "eventLog", title: "① EventLog", accent: "#3b82f6" },
  { key: "schedule", title: "② Schedule [DATA]", accent: "#d98a1e" },
  { key: "turboq", title: "③ Transactions", accent: "#16a34a" },
];

function TicketMasterTimeline({
  active, eventLog = [], schedule = [], turboq = [], errors = {}, loading = {},
  order = "asc", schedulerOnly = true,
}) {
  const { darkMode } = useContext(AppContext) || {};
  const rows = useMemo(
    () => groupTimeline({ eventLog, schedule, turboq }, { order, schedulerOnly }),
    [eventLog, schedule, turboq, order, schedulerOnly]
  );

  const [openRows, setOpenRows] = useState(() => new Set());
  const toggleRow = useCallback((key) => {
    setOpenRows((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  }, []);

  const [fr, setFr] = useState([1, 1, 1]);
  const containerRef = useRef(null);
  const drag = useRef(null);
  const onMove = useCallback((e) => {
    const d = drag.current;
    if (!d || !containerRef.current) return;
    const flexPx = containerRef.current.getBoundingClientRect().width - 16;
    if (flexPx <= 0) return;
    const totalFr = d.start[0] + d.start[1] + d.start[2];
    const deltaFr = ((e.clientX - d.startX) / flexPx) * totalFr;
    let left = d.start[d.i] + deltaFr, right = d.start[d.i + 1] - deltaFr;
    if (left < MIN_FR) { right -= MIN_FR - left; left = MIN_FR; }
    if (right < MIN_FR) { left -= MIN_FR - right; right = MIN_FR; }
    setFr((prev) => { const n = [...prev]; n[d.i] = left; n[d.i + 1] = right; return n; });
  }, []);
  const onUp = useCallback(() => {
    drag.current = null;
    window.removeEventListener("pointermove", onMove);
    window.removeEventListener("pointerup", onUp);
    document.body.style.userSelect = ""; document.body.style.cursor = "";
  }, [onMove]);
  const onDown = (i) => (e) => {
    e.preventDefault();
    drag.current = { i, startX: e.clientX, start: [...fr] };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    document.body.style.userSelect = "none"; document.body.style.cursor = "col-resize";
  };

  const template = `minmax(0,${fr[0]}fr) 8px minmax(0,${fr[1]}fr) 8px minmax(0,${fr[2]}fr)`;
  const anyError = LANES.some((l) => errors[l.key]);
  // #292 item 1 — big, obvious loading indicators (the tiny per-lane header
  // spinners were "hard to see"). ONE large centered spinner PER lane column,
  // each tied to that lane's own async query (EventLog · Schedule · TurboQ),
  // so you can see exactly which of the three table reads is still in flight.
  // Same GridSpinner the Time Machine grids use, dropped into each column.
  const anyLoading = Object.values(loading).some(Boolean);
  // Banding: light band on dark surface, dark band on light surface — visible both ways.
  const bandOdd = darkMode ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)";

  return (
    <div ref={containerRef} className="flex flex-col flex-1 min-h-0 border border-skin-base rounded-sm overflow-hidden">
      <div className="grid shrink-0" style={{ gridTemplateColumns: template }}>
        {LANES.map((l, i) => (
          <React.Fragment key={l.key}>
            <div className="px-2 py-1.5 flex items-center gap-2" style={{ backgroundColor: hexA(l.accent, 0.16) }}>
              <span className="text-sm font-semibold" style={{ color: l.accent }}>{l.title}</span>
              {loading[l.key] ? <span style={{ color: l.accent }}><MiniSpinner /></span> : null}
            </div>
            {i < LANES.length - 1 && (
              <div role="separator" onPointerDown={onDown(i)} title="Drag to resize" className="cursor-col-resize flex items-center justify-center" style={{ backgroundColor: hexA(LANES[i].accent, 0.16) }}>
                <div className="h-full w-px" style={{ backgroundColor: "rgba(128,128,128,0.5)" }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {anyError ? (
        <div className="shrink-0 px-2 py-1 text-[11px] flex flex-col gap-0.5" style={{ backgroundColor: "rgba(220,38,38,0.08)" }}>
          {LANES.filter((l) => errors[l.key]).map((l) => <div key={l.key} style={{ color: "#dc2626" }}>{l.title}: {errors[l.key]}</div>)}
        </div>
      ) : null}

      <div className="flex-1 min-h-0 overflow-y-auto relative">
        {/* Per-lane loading overlay: same column template as the rows, so each
            lane's large centered spinner sits directly over its own column. */}
        {anyLoading ? (
          <div className="absolute inset-0 z-50 grid pointer-events-none" style={{ gridTemplateColumns: template }}>
            {LANES.map((l, i) => (
              <React.Fragment key={l.key}>
                <div className="relative">
                  <GridSpinner show={Boolean(loading[l.key])} label={`Loading ${l.title.replace(/^\S+\s+/, "")}…`} />
                </div>
                {i < LANES.length - 1 ? <div /> : null}
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {!active ? (
          <div className="p-3 text-[11px] opacity-55">Enter an invoice and look it up — the correlated timeline renders here.</div>
        ) : rows.length === 0 ? (
          <div className="p-3 text-[11px] italic opacity-60">{Object.values(loading).some(Boolean) ? "Assembling timeline…" : "No correlated events found."}</div>
        ) : (
          rows.map((row, ri) => {
            const rowKey = `${row.sig}-${ri}`;
            const open = openRows.has(rowKey);
            const onToggle = () => toggleRow(rowKey);
            return (
              <div key={rowKey} className="grid"
                style={{ gridTemplateColumns: template, backgroundColor: ri % 2 ? bandOdd : "transparent", borderBottom: "1px solid rgba(128,128,128,0.18)" }}>
                <div className="px-1.5 py-1 min-w-0"><LaneCell lane="eventLog" items={row.cells.eventLog} open={open} onToggle={onToggle} /></div>
                <Div />
                <div className="px-1.5 py-1 min-w-0"><LaneCell lane="schedule" items={row.cells.schedule} open={open} onToggle={onToggle} /></div>
                <Div />
                <div className="px-1.5 py-1 min-w-0"><LaneCell lane="turboq" items={row.cells.turboq} open={open} onToggle={onToggle} /></div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function Div() {
  return <div className="flex justify-center"><div className="w-px h-full" style={{ backgroundColor: "rgba(128,128,128,0.18)" }} /></div>;
}
function hexA(hex, a) {
  const h = hex.replace("#", "");
  return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}, ${a})`;
}

export default TicketMasterTimeline;
